/* global BigInt */
import React, {useEffect, useRef, useState} from 'react';
import {prepareConfirmedPreorderPayment, readConfirmedPreorderPaymentStatus} from '../../../http/orderApi';

const unavailable = 'Подготовка оплаты предзаказа временно недоступна.';
const pendingMessage = 'Платёж проверяется. Не оплачивайте повторно.';
const refusedMessage = 'Банк отклонил оплату. Можно повторить попытку.';
const terminalMessages = {
  paid: 'Предзаказ оплачен.',
  review_required: 'Предзаказ требует проверки менеджером. Повторно не оплачивайте.',
  payment_cancelled: 'Банк отменил оплату. Для дальнейших действий свяжитесь с менеджером.',
  refunded: 'Оплата возвращена.',
  partially_refunded: 'Часть оплаты возвращена.',
};
const terminalOutcomes = Object.keys(terminalMessages);
const uuid = value => typeof value === 'string' && /^[a-f0-9]{8}-[a-f0-9]{4}-[1-8][a-f0-9]{3}-[89ab][a-f0-9]{3}-[a-f0-9]{12}$/.test(value);
const id = value => Number.isSafeInteger(value) && value > 0 && value <= 2147483647;
const transactionId = value => typeof value === 'string' && /^[1-9][0-9]{0,18}$/.test(value) && BigInt(value) <= 9223372036854775807n;
const text = value => typeof value === 'string' && value.length > 0 && value.length <= 128 &&
  value.trim() === value && !/[\u0000-\u001f\u007f-\u009f]/.test(value);
const fail = () => {throw new Error(unavailable);};
const paymentSlot = (owner, key) => id(owner) && uuid(key) ? `yw.preorder.payment-started.v1:${owner}:${key}` : null;
const wasStarted = slot => {
  if (!slot) return false;
  try {return window.localStorage.getItem(slot) !== null;} catch {return true;}
};
const spentSlot = (slot, transaction) => slot && transactionId(transaction) ? `${slot}:retry:${transaction}` : null;
const wasSpent = (slot, transaction) => {
  try {const key = spentSlot(slot, transaction); return !key || window.localStorage.getItem(key) !== null;} catch {return true;}
};
const rememberStarted = (slot, transaction = null) => {
  try {
    if (!slot) fail();
    if (transaction !== null) {
      const key = spentSlot(slot, transaction); if (!key) fail();
      window.localStorage.setItem(key, 'spent');
      if (window.localStorage.getItem(key) !== 'spent') fail();
    }
    const marker = transaction === null ? 'started' : `retry:${transaction}`;
    window.localStorage.setItem(slot, marker);
    if (window.localStorage.getItem(slot) !== marker) fail();
  } catch {fail();}
};
const forgetStarted = slot => {
  try {
    const marker = window.localStorage.getItem(slot);
    if (marker === 'started' || typeof marker === 'string' && marker.startsWith('retry:') && transactionId(marker.slice(6)))
      window.localStorage.removeItem(slot);
  } catch { /* Paid is a server fact; spent retry markers remain non-authority. */ }
};
const record = (raw, keys) => {
  if (!raw || ![Object.prototype, null].includes(Object.getPrototypeOf(raw))) fail();
  const descriptors = Object.getOwnPropertyDescriptors(raw);
  if (Reflect.ownKeys(descriptors).length !== keys.length) fail();
  return Object.fromEntries(keys.map(key => {
    const value = descriptors[key];
    if (!value?.enumerable || !Object.prototype.hasOwnProperty.call(value, 'value')) fail();
    return [key, value.value];
  }));
};
const moneyMinor = amount => {
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount < 0) fail();
  const minor = Math.round(amount * 100);
  if (!Number.isSafeInteger(minor) || minor / 100 !== amount) fail();
  return BigInt(minor);
};
const readReceipt = (raw, totalAmountMinor) => {
  const hasTax = raw && Object.prototype.hasOwnProperty.call(raw, 'taxationSystem');
  const value = record(raw, ['Items', 'email', 'phone', 'amounts', ...(hasTax ? ['taxationSystem'] : [])]);
  if (value.email !== null && (typeof value.email !== 'string' || value.email.length > 254 ||
    !/^[\x00-\x7f]+$/.test(value.email) || /[\u0000-\u001f\u007f]/.test(value.email) ||
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.email)) ||
    typeof value.phone !== 'string' || value.phone.trim() !== value.phone || !/^\+[1-9][0-9]{7,14}$/.test(value.phone) ||
    hasTax && (!Number.isInteger(value.taxationSystem) || value.taxationSystem < 0 || value.taxationSystem > 5)) fail();
  const rawItems = value.Items;
  if (!Array.isArray(rawItems) || Object.getPrototypeOf(rawItems) !== Array.prototype ||
    rawItems.length < 1 || rawItems.length > 501 || Reflect.ownKeys(rawItems).length !== rawItems.length + 1) fail();
  let sum = 0n, deliveryCount = 0;
  const Items = Array.from({length: rawItems.length}, (_, index) => {
    const descriptor = Object.getOwnPropertyDescriptor(rawItems, index);
    if (!descriptor || !Object.prototype.hasOwnProperty.call(descriptor, 'value')) fail();
    const item = record(descriptor.value, ['label', 'price', 'quantity', 'amount', 'vat', 'method', 'object']);
    if (!text(item.label) || !id(item.quantity) || item.method !== 1 || ![1, 4].includes(item.object) ||
      ![null, 0, 5, 7, 10, 22, 105, 107, 110, 122].includes(item.vat)) fail();
    const amount = moneyMinor(item.amount);
    if (moneyMinor(item.price) * BigInt(item.quantity) !== amount) fail();
    if (item.object === 4 && (++deliveryCount > 1 || item.quantity !== 1)) fail();
    sum += amount;
    return item;
  });
  const amounts = record(value.amounts, ['electronic', 'advancePayment', 'credit', 'provision']);
  if (sum !== BigInt(totalAmountMinor) || moneyMinor(amounts.electronic) !== sum ||
    amounts.advancePayment !== 0 || amounts.credit !== 0 || amounts.provision !== 0) fail();
  return {...value, Items, amounts};
};
const readPayment = (raw, checkout, principalId) => {
  const ready = raw && Object.getOwnPropertyDescriptor(raw, 'outcome')?.value === 'ready';
  const hasRetry = ready && Object.prototype.hasOwnProperty.call(raw, 'retryAfterTransactionId');
  const value = record(raw, ['schemaVersion', 'outcome', 'orderId', 'orderNumber', 'termsSha256',
    ...(ready ? ['currency', 'totalAmountMinor', 'invoiceId', 'accountId', 'publicId', 'paymentState', 'expiresAt', 'customerReceipt'] : []),
    ...(hasRetry ? ['retryAfterTransactionId'] : [])]);
  if (value.schemaVersion !== 'confirmed-preorder-payment-v1' || !id(value.orderId) || !id(value.orderNumber) ||
    value.orderId !== checkout.orderId || value.orderNumber !== checkout.orderNumber ||
    typeof value.termsSha256 !== 'string' || !/^[a-f0-9]{64}$/.test(value.termsSha256) ||
    value.termsSha256 !== checkout.termsSha256) fail();
  if (!ready) {if (!['processing', ...terminalOutcomes].includes(value.outcome)) fail(); return value;}
  if (value.currency !== 'RUB' || value.currency !== checkout.currency || value.totalAmountMinor !== checkout.totalAmountMinor ||
    typeof value.totalAmountMinor !== 'string' || !/^[1-9][0-9]*$/.test(value.totalAmountMinor) ||
    BigInt(value.totalAmountMinor) > BigInt(Number.MAX_SAFE_INTEGER) || value.paymentState !== 'unpaid' ||
    typeof value.invoiceId !== 'string' || !value.invoiceId.startsWith('preorder-attempt:') ||
    !uuid(value.invoiceId.slice('preorder-attempt:'.length)) || value.accountId !== `preorder-owner:${principalId}` ||
    !text(value.publicId) || typeof value.expiresAt !== 'string' || value.expiresAt.length !== 24 ||
    hasRetry && !transactionId(value.retryAfterTransactionId)) fail();
  const expires = Date.parse(value.expiresAt);
  if (!Number.isSafeInteger(expires) || new Date(expires).toISOString() !== value.expiresAt || expires <= Date.now()) fail();
  const amount = Number(value.totalAmountMinor) / 100;
  if (!Number.isFinite(amount) || Math.round(amount * 100) !== Number(value.totalAmountMinor)) fail();
  return {...value, amount, customerReceipt: readReceipt(value.customerReceipt, value.totalAmountMinor)};
};

const readStatus = (raw, checkout) => {
  const value = record(raw, ['schemaVersion', 'outcome', 'orderId', 'orderNumber', 'termsSha256']);
  if (value.schemaVersion !== 'confirmed-preorder-payment-status-v1' ||
    !['unpaid', 'processing', ...terminalOutcomes].includes(value.outcome) ||
    !id(value.orderId) || !id(value.orderNumber) || value.orderId !== checkout.orderId || value.orderNumber !== checkout.orderNumber ||
    typeof value.termsSha256 !== 'string' || !/^[a-f0-9]{64}$/.test(value.termsSha256) || value.termsSha256 !== checkout.termsSha256) fail();
  return value;
};

// Load the same SDK as ordinary checkout, only on an explicit payment action.
// No card fields or provider credentials enter this component or localStorage.
let sdkLoading;
const sdk = () => {
  if (typeof window.cp?.CloudPayments === 'function') return Promise.resolve(window.cp.CloudPayments);
  if (!sdkLoading) sdkLoading = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    const finish = failed => {
      clearTimeout(timer); script.onload = null; script.onerror = null;
      if (failed || typeof window.cp?.CloudPayments !== 'function') {script.remove(); reject(new Error(unavailable));}
      else resolve(window.cp.CloudPayments);
    };
    const timer = setTimeout(() => finish(true), 10000);
    script.src = 'https://widget.cloudpayments.ru/bundles/cloudpayments.js'; script.async = true;
    script.dataset.ywPreorderPayment = 'true'; script.onload = () => finish(false); script.onerror = () => finish(true);
    document.head.appendChild(script);
  }).finally(() => {sdkLoading = null;});
  return sdkLoading;
};

// The browser callback is not payment evidence. Only an owner-scoped server
// result can display paid; ambiguous results offer status checking, not a charge.
export default function ConfirmedPreorderPayment({checkout, acceptanceKey, principalId, onPaid,
  autoStart = false, buttonText = 'Оплатить предзаказ', showStatusAction = true, buttonClassName}) {
  const slot = paymentSlot(principalId, acceptanceKey);
  const receiptScope = JSON.stringify([principalId, acceptanceKey, checkout.orderId, checkout.orderNumber, checkout.termsSha256]);
  // Checkout mounts this continuation only after a fresh explicit submit and
  // acknowledged acceptance. Recovery/history leave autoStart off. Bind the
  // continuation to its original owner/receipt, never a later account render.
  const automaticIntent = useRef(autoStart === true ? receiptScope : null), automaticAttempted = useRef(false);
  const [phase, setPhase] = useState('checking'), [error, setError] = useState('');
  const alive = useRef(false), sequence = useRef(0), running = useRef(0), inFlight = useRef(false);
  const widgetOpen = useRef(false), widgetEntered = useRef(false), notified = useRef(false);
  const retryAfter = useRef(null), statusRequired = useRef(true);
  useEffect(() => {
    alive.current = true; sequence.current++; inFlight.current = false; widgetOpen.current = false;
    widgetEntered.current = wasStarted(slot); notified.current = false; retryAfter.current = null;
    statusRequired.current = true; setPhase('checking'); setError(''); checkStatus();
    return () => {alive.current = false; sequence.current++;};
  }, [acceptanceKey, principalId, checkout.orderId, checkout.orderNumber, checkout.termsSha256, slot]);
  // Opening a saved order is a read, not permission to create a bank attempt.
  // A current bound server fact must arrive before any payment action appears.
  const checkStatus = async () => {
    if (inFlight.current || widgetOpen.current) return;
    inFlight.current = true; statusRequired.current = true; setPhase('checking'); setError('');
    const token = sequence.current, requestId = ++running.current, current = () => alive.current && token === sequence.current;
    try {
      if (!uuid(acceptanceKey) || !id(principalId)) fail();
      const value = readStatus(await readConfirmedPreorderPaymentStatus({acceptanceKey,
        expectedTermsSha256: checkout.termsSha256}), checkout);
      if (!current()) return;
      statusRequired.current = false;
      setPhase(value.outcome === 'unpaid' ? widgetEntered.current || wasStarted(slot) ? 'confirming' : 'idle' : value.outcome);
      if (value.outcome === 'paid' && !notified.current) {notified.current = true; forgetStarted(slot); onPaid();}
    } catch (_) {
      if (current()) {setError(unavailable); setPhase('status_error');}
    } finally {if (current() && requestId === running.current) inFlight.current = false;}
  };
  const run = async (statusOnly = false, retry = false) => {
    if (statusRequired.current || inFlight.current || widgetOpen.current || terminalOutcomes.includes(phase)) return;
    const approvedRetry = retry ? retryAfter.current : null;
    retryAfter.current = null;
    inFlight.current = true; setError(''); setPhase(statusOnly ? 'confirming' : 'preparing');
    const token = sequence.current, requestId = ++running.current, current = () => alive.current && token === sequence.current;
    try {
      if (!uuid(acceptanceKey) || !id(principalId)) fail();
      if (retry && !transactionId(approvedRetry)) fail();
      const value = readPayment(await prepareConfirmedPreorderPayment({acceptanceKey,
        expectedTermsSha256: checkout.termsSha256}), checkout, principalId);
      if (!current()) return;
      if (value.outcome !== 'ready') {
        setPhase(value.outcome);
        if (value.outcome === 'paid' && !notified.current) {notified.current = true; forgetStarted(slot); onPaid();}
        return;
      }
      const offeredRetry = value.retryAfterTransactionId || null;
      if (offeredRetry !== null) {
        if (wasSpent(slot, offeredRetry)) {setPhase('confirming'); return;}
        // A status response only offers a button. Every explicit retry obtains
        // another fresh bound grant; changed/missing markers never charge.
        if (!retry || approvedRetry !== offeredRetry) {retryAfter.current = offeredRetry; setPhase('retry'); return;}
      } else if (retry || statusOnly || widgetEntered.current || wasStarted(slot)) {setPhase('confirming'); return;}
      const Constructor = await sdk();
      if (!current()) return;
      if (Date.parse(value.expiresAt) <= Date.now()) fail();
      const widget = new Constructor();
      if (typeof widget.pay !== 'function') fail();
      if (offeredRetry === null ? wasStarted(slot) : wasSpent(slot, offeredRetry)) {setPhase('confirming'); return;}
      // Only a minimal owner/key marker survives closing or reloading. It is
      // not evidence of payment and is cleared only after server confirmation.
      rememberStarted(slot, offeredRetry);
      let settled = false;
      const settle = success => {
        if (!current() || settled) return;
        settled = true; widgetOpen.current = false; inFlight.current = false; setPhase('confirming');
        if (success || !showStatusAction) run(true);
        else setError('Оплата не подтверждена. Проверьте статус предзаказа.');
      };
      widgetEntered.current = true; widgetOpen.current = true; setPhase('widget');
      widget.pay('auth', {publicId: value.publicId, description: `Предзаказ YOU WANNA №${value.orderNumber}`,
        amount: value.amount, currency: value.currency, invoiceId: value.invoiceId, accountId: value.accountId, skin: 'mini',
        ...(value.customerReceipt.email === null ? {} : {email: value.customerReceipt.email}),
        data: {CloudPayments: {CustomerReceipt: value.customerReceipt}}},
      {onSuccess: () => settle(true), onFail: () => settle(false), onComplete: () => settle(true)});
    } catch (_) {
      if (current()) {widgetOpen.current = false; setError(unavailable); setPhase(widgetEntered.current || statusOnly ? 'confirming' : 'idle');}
    } finally {if (current() && requestId === running.current) inFlight.current = false;}
  };
  useEffect(() => {
    if (showStatusAction) return;
    // New checkout has no manual status action. Re-entering the window reads
    // the owned result once; no timer and no new payment without a user action.
    const refresh = () => {
      if (inFlight.current || widgetOpen.current || terminalOutcomes.includes(phase) || phase === 'retry') return;
      automaticAttempted.current = true; // Focus is a read, never a deferred checkout click.
      checkStatus();
    };
    window.addEventListener('focus', refresh);
    return () => window.removeEventListener('focus', refresh);
  });
  useEffect(() => {
    if (autoStart !== true || automaticAttempted.current || automaticIntent.current !== receiptScope || phase !== 'idle' ||
      statusRequired.current || inFlight.current || widgetOpen.current || widgetEntered.current || wasStarted(slot)) return;
    automaticAttempted.current = true;
    run();
  });
  const busy = phase === 'checking' || phase === 'preparing' || phase === 'widget';
  return <div aria-label="Оплата предзаказа" aria-live="polite">
    {terminalOutcomes.includes(phase) ? <p role="status">{terminalMessages[phase]}</p>
      : <>
          {['confirming', 'processing'].includes(phase) && <p role="status">{pendingMessage}</p>}
          {phase === 'retry' && <p role="status">{refusedMessage}</p>}
          {error && <p role="alert">{error}</p>}
          {!showStatusAction && busy && <p role="status">{phase === 'checking' ? 'Проверяем оплату…' : 'Подготавливаем оплату…'}</p>}
          {(showStatusAction || phase === 'idle' || phase === 'retry') && <button type="button" className={buttonClassName} disabled={busy} onClick={() => phase === 'status_error' ? checkStatus()
            : run(phase !== 'idle' && phase !== 'retry', phase === 'retry')}>
            {phase === 'checking' ? 'Проверяем оплату…' : busy ? 'Подготавливаем оплату…'
              : phase === 'idle' ? buttonText : phase === 'retry' ? showStatusAction ? 'Повторить оплату' : buttonText : 'Проверить оплату'}
          </button>}
        </>}
  </div>;
}
